import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * Full-screen lightbox — ALL report pages stacked vertically and scrollable.
 *
 * Behaviour:
 *  - Renders every page of the report in one column. User scrolls naturally.
 *  - Opens scrolled to whichever page was clicked.
 *  - ESC or close button → close. Body scroll locked while open.
 */
export const ReportLightbox = ({ open, images, index, title, accent, onClose }) => {
  const scrollRef = useRef(null);

  // ESC to close
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  // On open: jump to the clicked page (no smooth — feels jarring on open)
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;
      const target = container.querySelector(`[data-page-index="${index || 0}"]`);
      if (target) container.scrollTop = target.offsetTop - 24;
    }, 50);
    return () => clearTimeout(t);
  }, [open, index]);

  if (!images || images.length === 0) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl"
          onClick={onClose}
          data-testid="report-lightbox"
        >
          {/* Top bar — title + close */}
          <div className="absolute top-0 left-0 right-0 p-5 md:p-7 flex items-center justify-between z-20 pointer-events-none">
            <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-1" style={{ color: accent }}>
                {title}
              </div>
              <div className="text-white/55 text-sm">
                {images.length} {images.length === 1 ? 'page' : 'pages'} · scroll to read
              </div>
            </div>
            <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={onClose}
                data-testid="lightbox-close"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 text-white/80 hover:text-white transition-colors"
                aria-label="Close lightbox"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable column of ALL pages */}
          <div
            ref={scrollRef}
            data-testid="lightbox-scroll-container"
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden pt-28 pb-12 px-4 md:px-10"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="mx-auto flex flex-col items-center gap-6 md:gap-8 max-w-5xl">
              {images.map((src, i) => (
                <div
                  key={src}
                  data-page-index={i}
                  className="relative w-full"
                >
                  <img
                    src={src}
                    alt={`${title} — page ${i + 1}`}
                    draggable={false}
                    className="block w-full h-auto rounded-xl border border-white/12"
                    style={{
                      boxShadow: `0 30px 80px -30px ${accent}55, 0 0 0 1px ${accent}22`,
                    }}
                  />
                  <div
                    className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.22em] font-semibold px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-md border border-white/15"
                    style={{ color: accent }}
                  >
                    {String(i + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ReportLightbox;
